import React from 'react';
import { Stack, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enUS, frFR } from '@mui/x-date-pickers/locales';
import { useTranslation } from 'react-i18next';
import { QualificationDraft, ScheduleError } from './qualification';
import SectionTitle from './SectionTitle';

type AppointmentField = 'startDate' | 'startTime' | 'endDate' | 'endTime';

interface AppointmentSlotProps {
  slot: 'start' | 'end';
  labelKey: string;
  date: Date | null;
  time: Date | null;
  dateField: AppointmentField;
  timeField: AppointmentField;
  hasError: boolean;
  onChange: (field: AppointmentField, value: Date | null) => void;
  /** Appelé au clic dans un des deux champs, qu'il passe par l'input ou par l'icône. */
  onActivate?: () => void;
}

function AppointmentSlot({
  slot,
  labelKey,
  date,
  time,
  dateField,
  timeField,
  hasError,
  onChange,
  onActivate
}: AppointmentSlotProps) {
  const { t } = useTranslation();
  const dateId = `qualification-appointment-${slot}-date`;
  const timeId = `qualification-appointment-${slot}-time`;

  return (
    <Stack spacing={1}>
      <Typography variant="body2">{t(labelKey)}</Typography>
      <Stack direction="row" spacing={1}>
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" component="label" htmlFor={dateId}>
            {t('interventionCreation.qualification.appointmentDate')}
          </Typography>
          <DesktopDatePicker
            value={date}
            onChange={(value) => onChange(dateField, value)}
            onOpen={onActivate}
            slotProps={{
              textField: {
                id: dateId,
                size: 'small',
                fullWidth: true,
                error: hasError,
                onFocus: onActivate
              }
            }}
          />
        </Stack>
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" component="label" htmlFor={timeId}>
            {t('interventionCreation.qualification.appointmentTime')}
          </Typography>
          <DesktopTimePicker
            value={time}
            onChange={(value) => onChange(timeField, value)}
            onOpen={onActivate}
            slotProps={{
              textField: {
                id: timeId,
                size: 'small',
                fullWidth: true,
                error: hasError,
                onFocus: onActivate
              }
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}

interface AppointmentSectionProps {
  draft: QualificationDraft;
  error: ScheduleError | null;
  onChange: (field: AppointmentField, value: Date | null) => void;
  /** Clic dans la fin du créneau, pour la préremplir à partir du début. */
  onEndFocus: () => void;
}

/** Créneau facultatif : début seul = au plus tôt, fin seule = au plus tard, les deux = entre. */
export default function AppointmentSection({
  draft,
  error,
  onChange,
  onEndFocus
}: AppointmentSectionProps) {
  const { t, i18n } = useTranslation();
  // Le LocalizationProvider de l'app fixe le format (fr) mais laisse les placeholders en
  // anglais (DD/MM/YYYY) : celui-ci, imbriqué, hérite de l'adaptateur et les traduit.
  const pickerLocale = i18n.language.startsWith('fr') ? frFR : enUS;

  return (
    <LocalizationProvider
      localeText={pickerLocale.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <SectionTitle>{t('interventionCreation.qualification.appointment')}</SectionTitle>

      <AppointmentSlot
        slot="start"
        labelKey="interventionCreation.qualification.appointmentStart"
        date={draft.startDate}
        time={draft.startTime}
        dateField="startDate"
        timeField="startTime"
        hasError={error === 'startDateMissing' || error === 'endBeforeStart'}
        onChange={onChange}
      />

      <AppointmentSlot
        slot="end"
        labelKey="interventionCreation.qualification.appointmentEnd"
        date={draft.endDate}
        time={draft.endTime}
        dateField="endDate"
        timeField="endTime"
        hasError={error === 'endDateMissing' || error === 'endBeforeStart'}
        onChange={onChange}
        onActivate={onEndFocus}
      />

      {error && (
        <Typography variant="caption" color="error" role="alert">
          {t(`interventionCreation.qualification.appointmentErrors.${error}`)}
        </Typography>
      )}
    </LocalizationProvider>
  );
}
